/* eslint-disable no-undef */
import {defineStore} from "pinia";
import axios from "axios";
import i18n from "@/locales/i18n";
import {useLocalStorage} from "@vueuse/core"
import {useToast} from "vue-toastification";
import Cookies from 'js-cookie'
import APIRequest from "@/libs/cloud_auth";
import { PERMISSIONS, ROLE_COLORS } from '@/config/roles';

const languages = [
    {code: "EN", name: "English"},
    {code: "PT", name: "Portuguese"},
    {code: "FR", name: "French"},
    {code: "DE", name: "Deutsch"},
    {code: "ES", name: "Spanish"},
    {code: "NL", name: "Dutch"},
]

export const useAppStore = defineStore("app", {
    state: () => ({
        appKey: false,
        appLoading: false,
        appLanguage: useLocalStorage('lang', 'EN'),
        selectedTime: 'day',
        selectedTenants: [],
        sidebar: useLocalStorage('sidebar_expanded', true),
        tenant: localStorage.getItem('tenant') ? JSON.parse(localStorage.getItem('tenant')) : null,
        hotels: [],
        cities: useLocalStorage('cities', Array),
        airports: useLocalStorage('airports', Array),
        session: {},
        fonts: [],
        settings: {
            date_format: "DD/MM/YYYY",
            timezone: "Europe/Lisbon",
            language: "EN",
            location: "Porto"
        },
        menu: [  
            {
                title: i18n.global.t('menu.players'),
                icon: 'Icon_players',
                link: 'players',
                roles: PERMISSIONS.players,
            },
            {
                title: i18n.global.t('menu.presentations'),
                icon: 'Icon_presentations',
                link: 'presentations',
                roles: PERMISSIONS.presentations,
            },
            {
                title: i18n.global.t('menu.media'),
                icon: 'Icon_media',
                roles: PERMISSIONS.media,
                submenu: [
                    {
                        title: i18n.global.t('menu.gallery'),
                        link: 'gallery',
                        roles: PERMISSIONS.media,
                    },
                    {
                        title: i18n.global.t('menu.playlists'),
                        link: 'playlists',
                        roles: PERMISSIONS.media,
                    }
                ]
            },
            {
                title: i18n.global.t('menu.conferences'),
                icon: 'Icon_conferences',
                link: 'conferences',
                roles: PERMISSIONS.conferences,
            },
            {
                title: i18n.global.t('menu.campaign'),
                icon: 'Icon_scheduler',
                link: 'scheduler',
                roles: PERMISSIONS.scheduler,
            },
            {
                title: i18n.global.t('menu.templates'),
                icon: 'Icon_groups',
                link: 'templates',
                roles: PERMISSIONS.templates,
            },
            {
                title: i18n.global.t('menu.diagnostics'),
                icon: 'Icon_diagnostics',
                roles: PERMISSIONS.diagnostics,
                submenu: [
                    {
                        title: i18n.global.t('menu.troubleshooting'),
                        link: 'troubleshooting',
                        roles: PERMISSIONS.diagnostics,
                    },
                    {
                        title: i18n.global.t('menu.logger'),
                        link: 'logger',
                        roles: PERMISSIONS.diagnostics,
                    },
                    {
                        title: i18n.global.t('menu.alertInbox'),
                        link: 'alert-inbox',
                        roles: PERMISSIONS.diagnostics,
                    }
                ]
            },
            {
                title: i18n.global.t('menu.analytics'),
                icon: 'Icon_analytics',
                roles: PERMISSIONS.analytics,
                submenu: [
                    {
                        title: i18n.global.t('menu.usage'),
                        link: 'usage',
                        roles: PERMISSIONS.analytics,
                    }
                ]
            },  
            {
                title: i18n.global.t('menu.settings'),
                icon: 'Icon_settings',
                roles: PERMISSIONS.settings,
                submenu: [
                    {
                        title: i18n.global.t('menu.regionalSettings'),
                        link: 'regional-settings',
                        roles: PERMISSIONS.settings,
                    },
                    {
                        title: i18n.global.t('menu.alerts'),
                        link: 'alerts',
                        roles: PERMISSIONS.diagnostics,
                    },
                ]
            },
             {
                 title: i18n.global.t('menu.softwareUpdate'),
                 icon: 'Icon_software',
                 roles: PERMISSIONS.software,
                 submenu: [
                     {
                         title: i18n.global.t('menu.management'),
                         link: 'management',
                         roles: PERMISSIONS.software,
                     },
                     {
                         title: i18n.global.t('menu.deviceList'),
                         link: 'devices',
                         roles: PERMISSIONS.software,
                     }
                 ]
             },
        ],
        appLanguages: languages,
        roleColors: ROLE_COLORS
    }),
    getters: {
        user() {
            if (!this.session) return null;
            const userData = this.session.user_data || {};
            const userObj = this.session.user || {};
            
            const email = userData.user_email || userData.email || this.session.email || userObj.email || this.session.username || "";
            const name = userData.user_name || userData.name || this.session.name || userObj.name || email.split('@')[0] || "";
            const picture = userData.picture || this.session.picture || userObj.picture || "";
            const identity_client_id = userData.identity_client_id || userObj.identity_client_id || "";
            const identity_domain = userData.identity_domain || userObj.identity_domain || "";

            
            return {
                name,
                email,
                picture,
                identity_client_id,
                identity_domain
            };
        },
        isAuthenticated() {
            if (!this.session) return false;
            const userData = this.session.user_data || {};
            const userObj = this.session.user || {};
            
            return !!(
                userData.user_email || 
                userData.email || 
                this.session.email || 
                userObj.email || 
                this.session.username
            );
        },
        getCurrentRole() {
            return this.hotels.find(hotel => hotel.ncm === this.tenant)?.role || null;
        },
        getCurrentTenant() {
            return this.tenant;
        },
        getTenantGroup() {
            return this.hotels.find(hotel => hotel.ncm === this.tenant)?.group_id || null;
        },
        getHotels(){
            return this.hotels.map((item) => {
                return { value: item.ncm, name: item.name };
            });
        },
        getFormattedHotels() {
            return (this.getHotels || []).map(hotel => ({
                ...hotel,
                originalName: hotel.name,
                name: `${hotel.value} ${hotel.name}`
            }))
        },
        getFormattedGroupHotels() {
            return (this.getGroupHotels || []).map(hotel => ({
                ...hotel,
                originalName: hotel.name,
                name: `${hotel.value} ${hotel.name}`
            }))
        },
        getListedHotels(){
            return this.hotels.map((item) => {
                return item.ncm;
            });
        },
        checkNoniusRoles(){
            if(!this.hotels) return false;
            return this.hotels.some(hotel => hotel.role.startsWith("nonius-"));
        },
        isNoniusAdmin() {
            return this.getCurrentRole.startsWith("nonius-");
        },
        getGroupHotels() {
            const groupId = this.getTenantGroup;
            if (!groupId) return [];

            const groupHotels = this.hotels
                .filter(hotel => hotel.group_id === groupId)
                .map(hotel => ({ value: String(hotel.ncm), name: hotel.name }));

            return groupHotels;
        }
    },
    actions: {
        async isAlive() {
            try {
                await axios.get(`alive/`);
                return true;
            } catch (error) {
                return false;
            }
        },
        async logDataUsage(logs){
            if (!logs?.length) return false;

            try {
                const bulkPayload = logs.map(log => JSON.stringify(log)).join('\n') + '\n';

                await APIRequest('metrics/send-to-elastic/', 'post', { data: bulkPayload });

                return true;
            } catch (error) {
                console.error("Elasticsearch Bulk Upload Failed:", error);
                return false;
            }
        },
        async getSettings() {
            try {
                const response = await APIRequest(`properties/settings/?tenant__ncm_id=` + this.getCurrentTenant);
                const result = response.data.results;
                if(!result || !result.length) return;
                
                this.settings = {
                    date_format: result[0].date_format.toUpperCase(),
                    timezone: result[0].timezone,
                    language: result[0].language,
                    location: result[0].location
                }

            } catch (error) {
                console.log(error);
            }
          },
        async fetchCityAndAirportData() {
            try {
              const [cityResponse, airportResponse] = await Promise.all([
                  axios.get(`/cityList/`),
                  axios.get(`/airports/`)
              ]);
              
              this.cities = cityResponse.data;
              this.airports = airportResponse.data;
            
            } catch (error) {
                console.log(error);
            }
          },
        async getCities(){
            try {
                const response = await axios.get(`cityList/`)
                return response.data
            } catch(error) {
                console.log(error)
            }
        },
        async getUserSession(){
            if (this.session && Object.keys(this.session).length > 0) {
                return this.session;
            }
            try {
                const isOtpRoute = Cookies.get('is_otp_route') === 'true';
                const endpoint = isOtpRoute ? 'auth/otp/exchange-token/' : 'auth/exchange-token/';

                const response = await axios.post(endpoint);
                
                this.session = response.data;
                return response.data;
            } catch(error) {
                console.error("Token validation/exchange failed:", error);
                this.session = {};
                return null;
            }
        },
        async getTenantsList() {
            try {
                const session = await this.getUserSession();
                if (!session) { 
                    useToast().error('An error occurred while fetching the session. Please try again.');
                    return false;
                }                
                
                let tenants = [];
                const userDetails = session.user_data?.user_details || {};
                Object.entries(userDetails).forEach(([roleKey, tenantList]) => {
                    let role = roleKey;
                    const prefixes = ["signage-legacy:", "signage:", "sigeo-legacy:", "sigeo:"];
                    const matchedPrefix = prefixes.find(p => roleKey.startsWith(p));
                    if (matchedPrefix) {
                        role = roleKey.substring(matchedPrefix.length);
                    }
                    if (Array.isArray(tenantList)) {
                        tenantList.forEach(tenantObj => {
                            if (tenantObj && typeof tenantObj === 'object') {
                                Object.entries(tenantObj).forEach(([code, name]) => {
                                    tenants.push({
                                        code,
                                        name,
                                        role
                                    });
                                });
                            }
                        });
                    }
                });

                // TESTING PURPOSES: CREATING FAKE ADMIN
                // ====== REMOVE ON PRODUCTION =======
                 if(process.env.VUE_APP_TITLE === 'Nonius Signage Local'){
                    tenants.push({
                        code: "NCM666",
                        name: "DEV TENANT",
                        role: "nonius-admin"
                    })

                    tenants.push({
                        code: "NONITECH",
                        name: "NONIUS TECH",
                        role: "nonius-technical"
                    })

                    tenants.push({
                        code: "NCMADMIN",
                        name: "Admin",
                        role: "client-admin"
                    })

                    tenants.push({
                        code: "NCMEVENTMANAGER",
                        name: "Event Manager",
                        role: "client-events-manager"
                    })

                    tenants.push({
                        code: "NCMTECH",
                        name: "Client Tech",
                        role: "client-tech"
                    })

                    tenants.push({
                        code: "NCMSALESMANAGER",
                        name: "Sales Manager",
                        role: "client-sales-manager"
                    })
                }

                let hotelList = [];
                tenants.forEach((tenant) => {
                    const { name, code, role } = tenant;
                    if (!hotelList.some(e => e.code === code && e.name === name && e.role === role)) {
                        hotelList.push({ role, ncm: code, name });
                    }
                });

                if (!hotelList.length) {
                    useToast().error("This user doesn't have any tenants.");
                    return false;
                }

                hotelList.sort((a, b) => {
                    return a.name.localeCompare(b.name)
                })

                this.hotels = hotelList

                const lastTenant = session.user_data?.last_tenant || userDetails.last_tenant;
                
                if (lastTenant && hotelList.some(h => String(h.ncm) === String(lastTenant))) {
                    this.tenant = lastTenant;
                    localStorage.setItem("tenant", JSON.stringify(this.tenant));
                } else if (hotelList.length > 0 && (!this.tenant || !hotelList.some(h => String(h.ncm) === String(this.tenant)))) {
                    this.tenant = hotelList[0].ncm;
                    localStorage.setItem("tenant", JSON.stringify(this.tenant));
                    this.updateUserLastSite(this.tenant);
                }

                return true;

            } catch (error) {
                console.log(error)
                return false;
            }

        },
        async setupGroups(){
            try {
                const result = new Map(await APIRequest(`properties/linked-tenant/`)
                .then(response => response.data.map(item => [item.ncm, item.group])));

                this.hotels.forEach(hotel => {
                    hotel.group_id = result.get(hotel.ncm.toString()) || "";
                });
                return true;
            } catch (error) {
                return false;
            }
        },
        async setupDB(user) {
            const response = await APIRequest(`properties/user/?email=${user.email}`)
            if (!response.data.results.length) {
                await APIRequest(`properties/user/`, 'post', {
                    "name": user.name,
                    "email": user.email
                })
            }
            return response.data
        },
        async setupTenantList() {
            try {
                const hotelList = this.hotels;

                // Fetch existing tenants with those NCMs.
                const ncmString = hotelList.map(hotel => hotel.ncm);
                const ncmObject = {
                    ids: ncmString
                };
                const response = await APIRequest('properties/tenant/get_by_ncm/', 'post', ncmObject)                
                const existingNCMs = new Set(response.data.map(tenant => tenant.ncm_id));

                // Filter new tenants that don't exist
                const newTenantsBatch = hotelList.reduce((batch, hotel) => {
                    if (!existingNCMs.has(String(hotel.ncm))) {
                        batch.push({ ncm_id: hotel.ncm, name: hotel.name });
                    }
                    return batch;
                }, []);
                
                // If there are new tenants to be added, batch insert them.
                if (newTenantsBatch.length) {
                    await APIRequest(`properties/tenant/`, 'post', newTenantsBatch);
                }

                return true;
            } catch (error) {
                console.error('Error in setupTenantList:', error);
                throw error;
            }
        },
        async setupAdmins(tenants) {
            try {
                const ncmList = tenants.map(tenant => String(tenant.ncm));
                const ncmObject = {
                    ids: ncmList
                };

                const response = await APIRequest('properties/linked-tenant/get_by_ncm/', 'post', ncmObject);
                let groupId = null;

                // Check if there are existing linked tenants with a group
                if (response.data.length) {
                    const found = response.data.find(result => result.group);
                    groupId = found.group;
                } else {
                    // If no linked tenants exist, create a new group
                    const newGroup = await APIRequest(`properties/group/`, 'post', {"name": 'Unnamed Group'});
                    groupId = newGroup.data.id;
                }

                // Filter out NCMs that already have a linked tenant
                const ncmToLink = ncmList.filter(ncm => !response.data.some(r => r.ncm === String(ncm)));

                // Prepare the data for batch creation of linked tenants
                const linkedTenantsBatch = ncmToLink.map(ncm => ({
                    "ncm": ncm,
                    "group": groupId
                }));

                // Batch create linked tenants
                if (linkedTenantsBatch.length) {
                    await APIRequest(`properties/linked-tenant/`, 'post', linkedTenantsBatch);
                }
            } catch (err) {
                console.error(err);
            }
        },
        getHotelName(ncm) {
            return this.hotels.find((x) => String(x.ncm) === String(ncm))?.name
        },
        getBucket(group) {
            if (!group)
                return this.tenant;
            else
                return this.hotels.find(hotel => hotel.ncm === this.tenant)?.group_id || null;
                // this.tenant.group_id
        },
        async setTenant(val) {
            this.tenant = await this.hotels.find(hotel => hotel.ncm === val).ncm || null;
            localStorage.setItem("tenant", JSON.stringify(this.tenant));
        },
        async setTenantSelect(val) {
            this.tenant = await this.hotels.find(hotel => hotel.ncm === val).ncm || null;
            localStorage.setItem("tenant", JSON.stringify(this.tenant));
        },
        toggleSidebar() {
            this.sidebar = !this.sidebar
        },
        getFirstRoute() {
            if (!this.tenant || !this.menu.find(item => item.roles.includes(this.getCurrentRole))) return '403'

            const accessibleLink = this.menu.flatMap(item => {
                if (!item.roles.includes(this.getCurrentRole)) return [];

                // If it has a submenu, filter that submenu for roles too
                if (item.submenu) {
                    return item.submenu.filter(sub => sub.roles.includes(this.getCurrentRole));
                }

                // Otherwise return the item itself
                return [item];
            })
            .find(item => item.link);

            return accessibleLink ? accessibleLink.link : null;

        },


        // IMPERSONATOR
        async getCloudUsers() {
            try {
                const response = await APIRequest('auth/cloud_users/');
                return response.data;

            } catch(error) {
                console.log(error)
            }  
        },
        async impersonateLogin(uuid, username, name, otp = false) {
            try {
                this.appLoading = true;
                const response = await APIRequest('auth/impersonate_login/', 'post', {
                    uuid,
                    username,
                    name,
                    otp
                });

                if(response.data["error"]){
                    this.appLoading = false;
                    useToast().error(i18n.global.t('toast.impersonateFailed'));
                    return false;
                }
                
                await this.getTenantsList();
                return true;
            } catch(error) {
                console.log(error)
                this.appLoading = false;
                return false;
            }
        },
        async impersonateLogout(email) {
            try {
                this.appLoading = true;
                await APIRequest('auth/impersonate_logout/', 'post', {
                    original_user_id: email
                });
                await this.getTenantsList();
                return true;
            } catch(error) {
                console.log(error)
                this.appLoading = false;
            }
            return false;
        },
        /* FONT MANAGER */
        async uploadFont(file) {
            try {
                let formData = new FormData();                
                // file, source_ref_id, source_ref_type
                formData.append("file", file);
                formData.append("source_ref_id", this.getCurrentTenant);
                formData.append("source_ref_type", "tenant");
 
                await this.uploadFontToS3(formData)
                return true;
            }
            catch (error) {
                console.log(error);
                return false;
            }            
        },
        async uploadFontToS3(formData){
            return new Promise((resolve, reject) => {
                const url = `media/font/upload_font/`;
                APIRequest(url,'post', formData, {
                  headers: {
                    "Content-Type": "multipart/form-data",
                  }
                })
                  .then(response => {
                    // Resolve the Promise with the response data
                    resolve(response.data);
                  })
                  .catch(error => {
                    // Reject the Promise with the error
                    reject(error);
                  });
              });
        },
        async getFonts() {
            try {
                // Remove previously injected font-face styles
                this.removeInjectedFonts();
        
                // Fetch fonts
                const response = await axios.get(
                    `media/font/?source_ref_id__ncm_id=${this.getCurrentTenant}`
                );
                const fonts = response.data;
                this.fonts = fonts;
        
                // Inject each font
                fonts.forEach((font) => this.injectFontFace(font));
        
                return fonts;
            } catch (error) {
                console.error("Error fetching fonts:", error);
            }
      },
  
      injectFontFace(font) {
        const fileName = font.name;
        const fontUrl = font.s3_url;
  
        // Use the filename (without extension) as font-family
        const fontFamily = `Font_${this.extractFontName(fileName).replace(/[^a-zA-Z0-9-_]/g, "_")}`; // e.g. "HappyFestive-MAYVn"
        const fontFormat = this.getFontFormat(fileName); // e.g. "truetype"
  
        const style = document.createElement("style");
        style.type = "text/css";
        style.setAttribute("data-font-face", "dynamic"); // Mark for cleanup
        style.innerText = `
                  @font-face {
                    font-family: "${fontFamily}";
                    src: url('${fontUrl}') format('${fontFormat}');
                    font-weight: normal;
                    font-style: normal;
                  }
                `;

        style.setAttribute("crossorigin", "anonymous");
        document.head.appendChild(style);
  
        document.fonts
          .load(`1em "${fontFamily}"`)
          .then(() => {
            console.log(`Font '${fontFamily}' loaded successfully`);
          })
          .catch((err) => {
            console.error(`Failed to load font '${fontFamily}'`, err);
          });
      },
  
      removeInjectedFonts() {
        document
          .querySelectorAll('style[data-font-face="dynamic"]')
          .forEach((el) => el.remove());
        document
          .querySelectorAll('link[data-font-preload="dynamic"]')
          .forEach((el) => el.remove());
      },
  
      extractFontName(fileName) {
        return fileName.replace(/\.[^/.]+$/, ""); // removes the extension
      },
  
      getFontFormat(fileName) {
        const ext = fileName.split(".").pop()?.toLowerCase();
        switch (ext) {
          case "woff":
            return "woff";
          case "woff2":
            return "woff2";
          case "ttf":
            return "truetype";
          case "otf":
            return "opentype";
          default:
            return "truetype"; // fallback
        }
      },
      removeFont(id){
        return new Promise((resolve, reject) => {
            APIRequest(`media/font/${id}/`, "delete")
            .then((response) => {
                this.fonts = this.fonts.filter((font) => font.id !== id);
                this.removeInjectedFonts();
                this.fonts.forEach((font) => this.injectFontFace(font));
                resolve(response.data);
            })
            .catch((error) => {
                console.error("Error removing font:", error);
                reject(error);
            });
        });
      },
      async updateUserLastSite(site) {
        try {
            const response = await APIRequest('user/update-last-server/', 'post', {last_logged_in_host: site});
            return response.data;
        } catch(error) {
            console.error(error)
        }
      }
    },
});
